import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import { TrainingAdminWorkspace } from '../../components/hr/TrainingAdminWorkspace';
import { DraftTrainingPreview } from '../../components/hr/TrainingPreviewModal';
// pages/hr/TrainingLibraryPage.tsx
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Input,
  Select,
  Option,
  Card,
  CardContent,
  CardOverflow,
  AspectRatio,
  Chip,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  Alert,
  FormControl,
  FormLabel,
  Stack,
  Textarea,
  Divider,
  Tabs,
  TabList,
  Tab,
  CircularProgress,
  Radio,
  RadioGroup,
  FormHelperText,
} from '@mui/joy';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import {
  MagnifyingGlass as SearchOutlined,
  Plus as AddOutlined,
  DotsThreeVertical as MoreVertOutlined,
  PlayCircle as PlayCircleOutlined,
  FilePdf as PictureAsPdfOutlined,
  LinkSimple as LinkOutlined,
  ListChecks as ChecklistOutlined,
  Question as QuizOutlined,
  Article as ArticleOutlined,
  PencilSimple as EditOutlined,
  Trash as DeleteOutlined,
  Copy as ContentCopyOutlined,
  Eye as VisibilityOutlined,
  Clock as AccessTimeOutlined,
  UsersThree as GroupsOutlined,
  CheckCircle as CheckCircleOutlined,
  Clock as PendingOutlined,
  Play as PlayOutlined,
  FileArrowUp as UploadFileOutlined,
  Image as ImageOutlined,
  X as CloseOutlined,
} from '@phosphor-icons/react';
import {
  useTrainingCategories,
  useCreateTrainingItem,
  useDeleteTrainingItem,
  useTrainingAssignments,
  useDeleteTrainingAssignment,
  useDuplicateTrainingItem,
  useHrSettings,
} from '../../hooks/useHr';
import { apiClient } from '../../lib/api';
import { useDebounce } from '../../hooks/useDebounce';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { TrainingLibraryItemListItem, TrainingContentType, CreateTrainingItemRequest, TrainingAssignmentListItem, AssignmentCriteria } from '../../types/hr';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { TrainingAssignmentModal } from '../../components/hr/TrainingAssignmentModal';
import { TrainingPreviewModal } from '../../components/hr/TrainingPreviewModal';
import {
  TrainingEditModal,
  StructuredContentEditor,
  CONTENT_TYPE_LABELS,
  CREDENTIAL_TYPE_LABELS,
  CREDENTIAL_TYPE_OPTIONS,
  UNPLAYABLE_CONTENT_TYPES,
  apiErrorMessage,
  contentTypeOptions,
  validateStructuredContent,
  type CredentialType,
} from '../../components/hr/TrainingEditModal';
import { TrainingProgressReports } from '../../components/hr/TrainingProgressReports';
import { CredentialRequirementsPanel } from '../../components/hr/CredentialRequirementsPanel';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';

const PAGE_SIZE = 20;

const contentTypeIcons: Record<TrainingContentType, React.ReactNode> = {
  video: <PlayCircleOutlined />,
  pdf: <PictureAsPdfOutlined />,
  link: <LinkOutlined />,
  checklist: <ChecklistOutlined />,
  quiz: <QuizOutlined />,
  article: <ArticleOutlined />,
  scorm: <PlayCircleOutlined />,
  presentation: <PictureAsPdfOutlined />,
};

const criteriaLabels: Record<AssignmentCriteria, string> = {
  all: 'All Team Members',
  w2: 'W-2 Only',
  '1099': '1099 Only',
  custom: 'Custom',
};

/** Library rows carry credential_type / validity_months, which types/hr.ts does not model yet. */
type LibraryRow = TrainingLibraryItemListItem & {
  credential_type?: CredentialType | '';
  validity_months?: number | null;
};

interface LibraryFilters {
  search?: string;
  category?: string;
  content_type?: string;
  credential_type?: string;
  page: number;
}

/**
 * One page of the library.
 *
 * Search, category and type all run server-side now. The page used to fetch a
 * single unpaginated page and filter it in the browser, so searching for a
 * course that lived on page 2 returned "No training items found".
 */
const useTrainingLibraryPage = (filters: LibraryFilters) =>
  useQuery({
    // Nested under 'training-library' so the existing create/update/delete
    // hooks' invalidateQueries(['training-library']) refreshes this page too.
    queryKey: ['training-library', 'page', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.content_type) params.append('content_type', filters.content_type);
      if (filters.credential_type) params.append('credential_type', filters.credential_type);
      params.append('page', String(filters.page));
      params.append('page_size', String(PAGE_SIZE));
      const response = await apiClient.get<{ count: number; results: LibraryRow[] }>(
        `/api/training/library/?${params.toString()}`
      );
      return response.data;
    },
  });

/** Apply an assignment rule now — enrols everyone it currently resolves to. Idempotent. */
const useRunAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; created_count: number }>(
        `/api/training/assignments/${id}/run/`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-progress'] });
      queryClient.invalidateQueries({ queryKey: ['training-progress-summary'] });
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });
};

type CreateTrainingItemForm = CreateTrainingItemRequest & {
  credential_type?: CredentialType | '';
  validity_months?: number | '';
};

const emptyCreateForm: CreateTrainingItemForm = {
  title: '',
  description: '',
  content_type: 'video',
  external_url: '',
  article_content: '',
  structured_content: [],
  estimated_duration_minutes: 0,
  category: '',
  credential_type: '',
  validity_months: '',
};

export const TrainingLibraryPage: React.FC = () => {
  const { user } = useAuth();
  const isHR = user?.is_hr || user?.is_organization_admin;
  const showUnplayableTypes = useFeatureFlag('training_scorm_enabled');

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<TrainingContentType | ''>('');
  const [credentialFilter, setCredentialFilter] = useState<CredentialType | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Modal states
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editAssignmentId, setEditAssignmentId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTrainingId, setPreviewTrainingId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTrainingId, setEditTrainingId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  // Page 3 of the old result set has nothing to do with the new one.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, categoryFilter, typeFilter, credentialFilter]);

  const { data: libraryPage, isLoading } = useTrainingLibraryPage({
    search: debouncedSearch || undefined,
    category: categoryFilter || undefined,
    content_type: typeFilter || undefined,
    credential_type: credentialFilter || undefined,
    page: currentPage,
  });
  const trainingItems = libraryPage?.results ?? [];
  const totalCount = libraryPage?.count ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const { data: categories = [] } = useTrainingCategories();
  const { data: assignments = [], isLoading: loadingAssignments, error: assignmentsError } = useTrainingAssignments();
  const { data: hrSettings } = useHrSettings();
  const createItem = useCreateTrainingItem();
  const deleteItem = useDeleteTrainingItem();
  const deleteAssignment = useDeleteTrainingAssignment();
  const duplicateItem = useDuplicateTrainingItem();
  const runAssignment = useRunAssignment();

  const [createForm, setCreateForm] = useState<CreateTrainingItemForm>(emptyCreateForm);
  const [createError, setCreateError] = useState('');
  const [videoSourceType, setVideoSourceType] = useState<'upload' | 'url'>('upload');

  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentPage, setAssignmentPage] = useState(1);
  const filteredAssignments = assignments.filter(a => `${a.name} ${a.description}`.toLowerCase().includes(assignmentSearch.toLowerCase()));
  const createContentType = createForm.content_type;

  const handleCreate = async () => {
    if (!createForm.title) {
      setCreateError('Title is required');
      return;
    }

    const structuredError = validateStructuredContent(
      createContentType,
      (createForm.structured_content as unknown[]) || []
    );
    if (structuredError) {
      setCreateError(structuredError);
      return;
    }

    // Questions/items only belong on the types that use them.
    const payload: CreateTrainingItemForm = { ...createForm };
    if (createContentType !== 'quiz' && createContentType !== 'checklist') {
      delete payload.structured_content;
    }

    try {
      await createItem.mutateAsync(payload);
      toast.success('Training item created successfully');
      setCreateModalOpen(false);
      setCreateForm(emptyCreateForm);
      setCreateError('');
      setVideoSourceType('upload');
    } catch (err) {
      setCreateError(apiErrorMessage(err, 'Failed to create training item'));
    }
  };

  const handleDelete = async (item: LibraryRow) => {
    if (!confirm(`Retire "${item.title}"? Learner assignments and completion history will be retained.`)) {
      return;
    }
    try {
      await deleteItem.mutateAsync(item.id);
      toast.success('Training item retired');
    } catch {
      toast.error('Failed to retire training item');
    }
  };

  const handlePreview = (itemId: string) => {
    setPreviewTrainingId(itemId);
    setPreviewModalOpen(true);
  };

  const handleEdit = (itemId: string) => {
    setEditTrainingId(itemId);
    setEditModalOpen(true);
  };

  const handleDuplicate = async (item: LibraryRow) => {
    try {
      await duplicateItem.mutateAsync(item.id);
      toast.success(`"${item.title}" duplicated successfully`);
    } catch {
      toast.error('Failed to duplicate training item');
    }
  };

  const handleEditAssignment = (assignmentId: string) => {
    setEditAssignmentId(assignmentId);
    setAssignmentModalOpen(true);
  };

  const handleRunAssignment = async (assignment: TrainingAssignmentListItem) => {
    try {
      const result = await runAssignment.mutateAsync(assignment.id);
      toast.success(result.message);
    } catch {
      toast.error('Failed to run assignment');
    }
  };

  const handleDeleteAssignment = async (assignment: TrainingAssignmentListItem) => {
    if (!confirm(`Are you sure you want to delete "${assignment.name}"?`)) {
      return;
    }
    try {
      await deleteAssignment.mutateAsync(assignment.id);
      toast.success('Assignment deleted');
    } catch {
      toast.error('Failed to delete assignment');
    }
  };

  const handleCreateAssignment = () => {
    setEditAssignmentId(null);
    setAssignmentModalOpen(true);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Training Library</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" color="neutral">
            Add training, assign it to your team, and track completion and expiring credentials.
          </Typography>
        </Box>
        {isHR && (
          <Button
            startDecorator={<AddOutlined />}
            onClick={() => {
              setCreateForm(emptyCreateForm);
              setCreateError('');
              setCreateModalOpen(true);
            }}
          >
            Add Training
          </Button>
        )}
      </Stack>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)} sx={{ mb: 3 }}>
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
          <Tab>Library</Tab>
          {isHR && <Tab>Assignments</Tab>}
          {isHR && <Tab>Compliance</Tab>}
          {isHR && <Tab>Requirements</Tab>}
          {isHR && <Tab>Paths & follow-up</Tab>}
        </TabList>
      </Tabs>

      {activeTab === 4 && isHR && <TrainingAdminWorkspace />}
      {activeTab === 0 && (
        <>
          {/* Filters */}
          <CollapsibleFilters><Card sx={{ mb: 3, p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Input
                placeholder="Search training..." aria-label="Search training"
                startDecorator={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: { xs: 0, sm: 250 }, flex: { xs: '1 1 100%', sm: '1 1 250px' } }}
              />
              <Select
                placeholder="Category" aria-label="Training category"
                value={categoryFilter}
                onChange={(_, value) => setCategoryFilter(value as string)}
                sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 150px' } }}
              >
                <Option value="">All Categories</Option>
                {categories.map((cat) => (
                  <Option key={cat} value={cat}>{cat}</Option>
                ))}
              </Select>
              <Select
                placeholder="Type" aria-label="Training type"
                value={typeFilter}
                onChange={(_, value) => setTypeFilter(value as TrainingContentType | '')}
                sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 calc(50% - 6px)', sm: '0 0 150px' } }}
              >
                <Option value="">All Types</Option>
                {contentTypeOptions(showUnplayableTypes).map((type) => (
                  <Option key={type} value={type}>{CONTENT_TYPE_LABELS[type]}</Option>
                ))}
              </Select>
              <Select
                placeholder="Credential" aria-label="Training credential"
                value={credentialFilter}
                onChange={(_, value) => setCredentialFilter((value as CredentialType) || '')}
                sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: '1 1 100%', sm: '0 0 180px' } }}
              >
                <Option value="">All Credentials</Option>
                {CREDENTIAL_TYPE_OPTIONS.map(([value, label]) => (
                  <Option key={value} value={value}>{label}</Option>
                ))}
              </Select>
            </Stack>
          </Card></CollapsibleFilters>

          {/* Training Items Grid */}
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>Loading...</Box>
          ) : trainingItems.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <Typography level="body-lg">No training items found</Typography>
              <Typography level="body-sm" color="neutral">
                {isHR ? 'Click "Add Training" to create your first training item' : 'No training has been assigned to you yet'}
              </Typography>
            </Card>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' }, gap: 2 }}>
              {trainingItems.map((item) => (
                <Card key={item.id} variant="outlined">
                  <CardOverflow>
                    <AspectRatio ratio="16/9">
                      {item.thumbnail ? (
                        <img src={item.thumbnail} alt={item.title} />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'neutral.100',
                            color: 'neutral.500',
                          }}
                        >
                          {contentTypeIcons[item.content_type]}
                        </Box>
                      )}
                    </AspectRatio>
                  </CardOverflow>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-md" noWrap>{item.title}</Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                          {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                        </Typography>
                      </Box>
                      {isHR && (
                        <Dropdown>
                          <MenuButton
                            slots={{ root: IconButton }}
                            slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                          >
                            <MoreVertOutlined />
                          </MenuButton>
                          <Menu placement="bottom-end">
                            <MenuItem onClick={() => handlePreview(item.id)}>
                              <VisibilityOutlined style={{ marginRight: 8 }} />
                              Preview
                            </MenuItem>
                            <MenuItem onClick={() => handleEdit(item.id)}>
                              <EditOutlined style={{ marginRight: 8 }} />
                              Edit
                            </MenuItem>
                            <MenuItem onClick={() => handleDuplicate(item)}>
                              <ContentCopyOutlined style={{ marginRight: 8 }} />
                              Duplicate
                            </MenuItem>
                            <Divider />
                            <MenuItem color="danger" onClick={() => handleDelete(item)}>
                              <DeleteOutlined style={{ marginRight: 8 }} />
                              Delete
                            </MenuItem>
                          </Menu>
                        </Dropdown>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Chip
                        size="sm"
                        variant="soft"
                        startDecorator={contentTypeIcons[item.content_type]}
                      >
                        {CONTENT_TYPE_LABELS[item.content_type]}
                      </Chip>
                      {item.credential_type && (
                        <Chip size="sm" variant="soft" color="warning">
                          {CREDENTIAL_TYPE_LABELS[item.credential_type]}
                          {item.validity_months ? ` · ${item.validity_months} mo` : ''}
                        </Chip>
                      )}
                      {item.category && (
                        <Chip size="sm" variant="outlined">
                          {item.category}
                        </Chip>
                      )}
                      {item.estimated_duration_minutes > 0 && (
                        <Chip size="sm" variant="plain" startDecorator={<AccessTimeOutlined />}>
                          {item.estimated_duration_minutes} min
                        </Chip>
                      )}
                    </Box>

                    {isHR && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Typography level="body-xs" color="neutral">
                          {item.view_count} views
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {item.completion_count} completions
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
              }}
            >
              <Typography level="body-sm" color="neutral">
                {`Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, totalCount)}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount} training items`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      {activeTab === 1 && isHR && (
        <Box>
          {/* Assignments Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
          >
            <Box>
              <Typography level="title-lg">Training Assignments</Typography>
              <Typography level="body-sm" color="neutral">
                Create and manage training assignments for your team
              </Typography>
            </Box>
            <Button startDecorator={<AddOutlined />} onClick={handleCreateAssignment}>
              Create Assignment
            </Button>
          </Stack>

          <Input placeholder="Search all assignments" value={assignmentSearch} onChange={e => { setAssignmentSearch(e.target.value); setAssignmentPage(1); }} />
          {assignmentsError && <Alert color="danger">Assignments could not load. Reload to retry.</Alert>}
          <Stack direction="row" spacing={1} sx={{ my: 2 }}><Button disabled={assignmentPage <= 1} onClick={() => setAssignmentPage(assignmentPage - 1)}>Previous</Button><Typography>{filteredAssignments.length} assignments · Page {assignmentPage}</Typography><Button disabled={assignmentPage * 20 >= filteredAssignments.length} onClick={() => setAssignmentPage(assignmentPage + 1)}>Next</Button></Stack>
          {/* Assignments List */}
          {loadingAssignments ? (
            <Card>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            </Card>
          ) : assignments.length === 0 ? (
            <Card>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <GroupsOutlined size={48} color="var(--joy-palette-neutral-400)" style={{ marginBottom: 8 }} />
                <Typography level="body-lg">No training assignments</Typography>
                <Typography level="body-sm" color="neutral">
                  Create your first assignment to start assigning training to team members
                </Typography>
                <Button variant="soft" sx={{ mt: 2 }} onClick={handleCreateAssignment}>
                  Create Assignment
                </Button>
              </Box>
            </Card>
          ) : (
            <Card>
              <ResponsiveTable<TrainingAssignmentListItem>
                rows={filteredAssignments.slice((assignmentPage - 1) * 20, assignmentPage * 20)}
                getRowKey={(a) => a.id}
                columns={[
                  {
                    key: 'assignment',
                    sortValue: (row) => row.name,
                    header: 'Assignment',
                    mobileVariant: 'header',
                    render: (assignment) => (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
                          {assignment.name}
                        </Typography>
                        {assignment.description && (
                          <Typography level="body-xs" color="neutral">
                            {assignment.description.substring(0, 80)}
                            {assignment.description.length > 80 ? '...' : ''}
                          </Typography>
                        )}
                      </Box>
                    ),
                  },
                  {
                    key: 'criteria',
                    header: 'Criteria',
                    render: (assignment) => (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="sm" variant="soft">
                          {criteriaLabels[assignment.criteria]}
                        </Chip>
                        {assignment.is_onboarding && (
                          <Chip size="sm" variant="soft" color="primary">
                            Onboarding
                          </Chip>
                        )}
                      </Box>
                    ),
                  },
                  {
                    key: 'training_items',
                    sortValue: (row) => row.training_item_count,
                    header: 'Training Items',
                    render: (assignment) => (
                      <Typography level="body-sm">{assignment.training_item_count} items</Typography>
                    ),
                  },
                  {
                    key: 'assigned',
                    sortValue: (row) => row.assigned_count,
                    header: 'People Enrolled',
                    render: (assignment) => (
                      <Typography level="body-sm">{assignment.assigned_count}</Typography>
                    ),
                  },
                  {
                    key: 'due_after',
                    sortValue: (row) => row.due_days_after_start,
                    header: 'Due After',
                    render: (assignment) => (
                      <Typography level="body-sm">{assignment.due_days_after_start} days</Typography>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (assignment) => (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          size="sm"
                          color={assignment.is_active ? 'success' : 'neutral'}
                          variant="soft"
                          startDecorator={assignment.is_active ? <CheckCircleOutlined /> : <PendingOutlined />}
                        >
                          {assignment.is_active ? 'Active' : 'Inactive'}
                        </Chip>
                        {assignment.is_mandatory && (
                          <Chip size="sm" variant="outlined" color="warning">
                            Mandatory
                          </Chip>
                        )}
                      </Box>
                    ),
                  },
                ]}
                rowActions={(assignment) => (
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                    >
                      <MoreVertOutlined />
                    </MenuButton>
                    <Menu placement="bottom-end">
                      <MenuItem onClick={() => handleEditAssignment(assignment.id)}>
                        <EditOutlined style={{ marginRight: 8 }} />
                        Edit
                      </MenuItem>
                      <MenuItem
                        disabled={!assignment.is_active}
                        onClick={() => handleRunAssignment(assignment)}
                      >
                        <PlayOutlined style={{ marginRight: 8 }} />
                        Run now
                      </MenuItem>
                      <Divider />
                      <MenuItem color="danger" onClick={() => handleDeleteAssignment(assignment)}>
                        <DeleteOutlined style={{ marginRight: 8 }} />
                        Delete
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                )}
              />
            </Card>
          )}
        </Box>
      )}

      {activeTab === 2 && isHR && (
        <TrainingProgressReports />
      )}

      {activeTab === 3 && isHR && (
        <CredentialRequirementsPanel />
      )}

      {/* Create Training Modal */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <ModalDialog sx={{ width: { xs: 'auto', sm: 560 }, maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Add Training Item</Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            {createError && <Alert color="danger">{createError}</Alert>}

            <FormControl required>
              <FormLabel>Title</FormLabel>
              <Input
                value={createForm.title}
                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder="Training title"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={createForm.description || ''}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Brief description of the training"
                minRows={2}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Content Type</FormLabel>
              <Select
                value={createContentType}
                onChange={(_, value) => {
                  setCreateForm({
                    ...createForm,
                    content_type: value as TrainingContentType,
                    file: undefined,
                    external_url: '',
                    structured_content: [],
                    passing_score:
                      value === 'quiz' ? hrSettings?.quiz_passing_score ?? 70 : undefined,
                  });
                  setVideoSourceType('upload');
                }}
              >
                {contentTypeOptions(showUnplayableTypes).map((type) => (
                  <Option key={type} value={type}>
                    {CONTENT_TYPE_LABELS[type]}
                  </Option>
                ))}
              </Select>
              {UNPLAYABLE_CONTENT_TYPES.includes(createContentType) && (
                <FormHelperText>
                  This type has no player yet — team members will not be able to open it.
                </FormHelperText>
              )}
            </FormControl>

            {/* Video type - Upload or URL options */}
            {createContentType === 'video' && (
              <>
                <FormControl>
                  <FormLabel>Video Source</FormLabel>
                  <RadioGroup
                    orientation="horizontal"
                    value={videoSourceType}
                    onChange={(e) => {
                      setVideoSourceType(e.target.value as 'upload' | 'url');
                      setCreateForm({ ...createForm, file: undefined, external_url: '' });
                    }}
                  >
                    <Radio value="upload" label="Upload File" />
                    <Radio value="url" label="YouTube/Vimeo URL" />
                  </RadioGroup>
                </FormControl>

                {videoSourceType === 'upload' ? (
                  <FormControl>
                    <FormLabel>Video File</FormLabel>
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: createForm.file ? 'primary.400' : 'neutral.300',
                        borderRadius: 'sm',
                        p: 2,
                        textAlign: 'center',
                        bgcolor: createForm.file ? 'primary.50' : 'transparent',
                      }}
                    >
                      {createForm.file ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PlayCircleOutlined color="var(--joy-palette-primary-500)" />
                          <Typography level="body-sm">{createForm.file.name}</Typography>
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => setCreateForm({ ...createForm, file: undefined })}
                          >
                            <CloseOutlined />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          component="label"
                          variant="soft"
                          startDecorator={<UploadFileOutlined />}
                        >
                          Choose Video File
                          <input
                            type="file"
                            hidden
                            accept="video/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setCreateForm({ ...createForm, file });
                            }}
                          />
                        </Button>
                      )}
                    </Box>
                    <FormHelperText>Supports MP4, WebM, MOV formats</FormHelperText>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel>YouTube/Vimeo URL</FormLabel>
                    <Input
                      value={createForm.external_url || ''}
                      onChange={(e) => setCreateForm({ ...createForm, external_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                    />
                    <FormHelperText>Paste a YouTube or Vimeo video link</FormHelperText>
                  </FormControl>
                )}
              </>
            )}

            {/* PDF type - File upload */}
            {createContentType === 'pdf' && (
              <FormControl>
                <FormLabel>PDF File</FormLabel>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: createForm.file ? 'primary.400' : 'neutral.300',
                    borderRadius: 'sm',
                    p: 2,
                    textAlign: 'center',
                    bgcolor: createForm.file ? 'primary.50' : 'transparent',
                  }}
                >
                  {createForm.file ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <PictureAsPdfOutlined color="var(--joy-palette-danger-500)" />
                      <Typography level="body-sm">{createForm.file.name}</Typography>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => setCreateForm({ ...createForm, file: undefined })}
                      >
                        <CloseOutlined />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      variant="soft"
                      startDecorator={<UploadFileOutlined />}
                    >
                      Choose PDF File
                      <input
                        type="file"
                        hidden
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setCreateForm({ ...createForm, file });
                        }}
                      />
                    </Button>
                  )}
                </Box>
              </FormControl>
            )}

            {createContentType === 'link' && (
              <FormControl>
                <FormLabel>External URL</FormLabel>
                <Input
                  value={createForm.external_url || ''}
                  onChange={(e) => setCreateForm({ ...createForm, external_url: e.target.value })}
                  placeholder="https://..."
                />
              </FormControl>
            )}

            {createContentType === 'article' && (
              <FormControl>
                <FormLabel>Article Content</FormLabel>
                <Textarea
                  value={createForm.article_content || ''}
                  onChange={(e) => setCreateForm({ ...createForm, article_content: e.target.value })}
                  placeholder="Write your article content here..."
                  minRows={6}
                />
              </FormControl>
            )}

            {/* Checklist / Quiz authoring — a quiz with no questions is rejected
                by the API, and used to pass everybody at 100%. */}
            {(createContentType === 'checklist' || createContentType === 'quiz') && (
              <FormControl>
                <FormLabel>{createContentType === 'quiz' ? 'Questions' : 'Checklist Items'}</FormLabel>
                <StructuredContentEditor
                  contentType={createContentType}
                  value={(createForm.structured_content as unknown[]) || []}
                  onChange={(next) => setCreateForm({ ...createForm, structured_content: next })}
                />
              </FormControl>
            )}

            {createContentType === 'quiz' && (
              <FormControl sx={{ maxWidth: 200 }}>
                <FormLabel>Passing Score (%)</FormLabel>
                <Input
                  type="number"
                  value={createForm.passing_score ?? hrSettings?.quiz_passing_score ?? 70}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, passing_score: parseInt(e.target.value) || 0 })
                  }
                  slotProps={{ input: { min: 0, max: 100 } }}
                />
                <FormHelperText>Defaults to the org setting in HR Settings.</FormHelperText>
              </FormControl>
            )}

            {/* Thumbnail picker for all types */}
            <FormControl>
              <FormLabel>Thumbnail (Optional)</FormLabel>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: createForm.thumbnail ? 'primary.400' : 'neutral.300',
                  borderRadius: 'sm',
                  p: 2,
                  textAlign: 'center',
                  bgcolor: createForm.thumbnail ? 'primary.50' : 'transparent',
                }}
              >
                {createForm.thumbnail ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <ImageOutlined color="var(--joy-palette-primary-500)" />
                    <Typography level="body-sm">{createForm.thumbnail.name}</Typography>
                    <IconButton
                      size="sm"
                      variant="plain"
                      onClick={() => setCreateForm({ ...createForm, thumbnail: undefined })}
                    >
                      <CloseOutlined />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    component="label"
                    variant="soft"
                    color="neutral"
                    size="sm"
                    startDecorator={<ImageOutlined />}
                  >
                    Add Thumbnail
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setCreateForm({ ...createForm, thumbnail: file });
                      }}
                    />
                  </Button>
                )}
              </Box>
              <FormHelperText>Optional cover image for the training item</FormHelperText>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Category</FormLabel>
                <Input
                  value={createForm.category || ''}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  placeholder="e.g., Safety, Sales, etc."
                />
              </FormControl>
              <FormControl sx={{ width: { xs: '100%', sm: 120 } }}>
                <FormLabel>Duration (min)</FormLabel>
                <Input
                  type="number"
                  value={createForm.estimated_duration_minutes || ''}
                  onChange={(e) => setCreateForm({ ...createForm, estimated_duration_minutes: parseInt(e.target.value) || 0 })}
                />
              </FormControl>
            </Box>

            {/* Credential — what puts this item on the compliance wall */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Safety Credential</FormLabel>
                <Select
                  value={createForm.credential_type || ''}
                  onChange={(_, value) =>
                    setCreateForm({ ...createForm, credential_type: (value as CredentialType) || '' })
                  }
                >
                  <Option value="">Not a credential</Option>
                  {CREDENTIAL_TYPE_OPTIONS.map(([value, label]) => (
                    <Option key={value} value={value}>{label}</Option>
                  ))}
                </Select>
                <FormHelperText>Tagged items become a column on the compliance wall.</FormHelperText>
              </FormControl>
              <FormControl sx={{ width: { xs: '100%', sm: 160 } }}>
                <FormLabel>Valid For (months)</FormLabel>
                <Input
                  type="number"
                  value={createForm.validity_months ?? ''}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      validity_months: e.target.value === '' ? '' : parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="Never expires"
                  slotProps={{ input: { min: 1, max: 120 } }}
                />
                <FormHelperText>Blank never expires</FormHelperText>
              </FormControl>
            </Box>

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'flex-end', mt: 2 }}
            >
              <Button variant="outlined" color="neutral" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <DraftTrainingPreview draft={createForm} />
              <Button onClick={handleCreate} loading={createItem.isPending}>
                Create
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Training Assignment Modal */}
      <TrainingAssignmentModal
        open={assignmentModalOpen}
        onClose={() => {
          setAssignmentModalOpen(false);
          setEditAssignmentId(null);
        }}
        assignmentId={editAssignmentId}
        onSuccess={() => toast.success(editAssignmentId ? 'Assignment updated' : 'Assignment created')}
      />

      {/* Training Preview Modal */}
      <TrainingPreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewTrainingId(null);
        }}
        trainingId={previewTrainingId}
      />

      {/* Training Edit Modal */}
      <TrainingEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditTrainingId(null);
        }}
        trainingId={editTrainingId}
        onSuccess={() => toast.success('Training item updated')}
      />
    </Box>
  );
};

export default TrainingLibraryPage;
